import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Activity, ChevronDown, ChevronUp, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

const STATUS_PILL = {
  pending: 'bg-amber-500/15 text-amber-700 border-amber-500/40',
  running: 'bg-blue-500/15 text-blue-700 border-blue-500/40',
  completed: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/40',
  failed: 'bg-red-500/15 text-red-700 border-red-500/40',
};

const STATUS_ICON = {
  pending: <Loader2 className="h-3 w-3 animate-pulse" />,
  running: <Loader2 className="h-3 w-3 animate-spin" />,
  completed: <CheckCircle2 className="h-3 w-3" />,
  failed: <XCircle className="h-3 w-3" />,
};

/**
 * Floating dock at the bottom-right of the Payroll page that polls
 * /hr/payroll/jobs every 10s and shows the latest 5 background jobs as chips.
 * Collapsible. Auto-hides if there are no recent jobs.
 */
export default function PayrollJobsDock() {
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/hr/payroll/jobs?limit=5');
      setJobs(res.data?.items || []);
    } catch {
      // silent fail — dock simply hides if backend unavailable
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  if (!jobs.length) return null;

  const active = jobs.some(j => j.status === 'running' || j.status === 'pending');

  return (
    <div className="fixed bottom-4 right-4 z-40 w-[340px]" data-testid="payroll-jobs-dock">
      <Card className="shadow-xl border-2">
        <CardContent className="p-2 space-y-1.5">
          <button
            onClick={() => setOpen(o => !o)}
            className="flex items-center justify-between w-full text-xs font-semibold hover:bg-muted/40 rounded p-1"
          >
            <span className="flex items-center gap-1.5">
              <Activity className={`h-3.5 w-3.5 ${active ? 'text-blue-600 animate-pulse' : 'text-muted-foreground'}`} />
              Payroll Jobs ({jobs.length})
            </span>
            {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
          </button>
          {open && (
            <div className="space-y-1">
              {jobs.map(j => (
                <div key={j.id} className="flex items-center justify-between text-[11px] border-t pt-1.5"
                  data-testid={`payroll-job-${j.id}`}
                >
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className={`flex items-center gap-1 px-1.5 py-0.5 rounded border whitespace-nowrap ${STATUS_PILL[j.status] || ''}`}>
                      {STATUS_ICON[j.status]} {j.status}
                    </span>
                    <span className="font-mono">{j.month}</span>
                  </div>
                  <span className="text-muted-foreground truncate ml-2">
                    {j.status === 'completed' && j.result_summary?.employee_count
                      ? `${j.result_summary.employee_count} emp`
                      : j.status === 'failed' && j.error
                      ? (j.error.slice(0, 30) + (j.error.length > 30 ? '…' : ''))
                      : new Date(j.created_at).toLocaleTimeString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
